import { buildPrompt } from '@/ai/utils/promptBuilder'
import { functionType } from '@/composables/useWorkflowAssistantFunctions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { constructMessagesHistory } from '@/ai/prompts/common/messagesHistory'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import markdownContent from '@/ai/prompts/markdown/systemPromptWorkflowAssistant.md'
import brand from '@/composables/useBrand'

const cachedSpec = { key: null, value: null }

const systemPrompt = markdownContent
  .replaceAll('{BRAND_NAME_UPPER}', brand.name.toUpperCase())
  .replaceAll('{BRAND_NAME}', brand.name)

const buildAssistantWorkflowSpec = (workflow, boundedContext, cardTypesMap) => {
  const key = `${workflow.id}:${workflow.version}`

  if (cachedSpec.key === key) {
    return cachedSpec.value
  }

  const spec = buildWorkflowSpec(workflow, boundedContext, cardTypesMap, true) || '{}'
  cachedSpec.key = key
  cachedSpec.value = spec

  return spec
}

export const buildWorkflowAssistantPrompt = ({ workflow, messagesHistory, prompt, currentUIState, currentTurnOutputs = [], collaboratorDrift = false }) => {
  const messages = []

  const cardTypes = workflow.cardTypes || []
  const cardTypesMap = buildCardTypesMap(cardTypes)
  const boundedContext = (workflow.boundedContexts || [])[0] || null

  // The static/dynamic split is the provider cache boundary; only the dynamic part changes per turn.
  const staticPromptSections = []
  const dynamicPromptSections = []

  staticPromptSections.push({
    content: systemPrompt,
    flattenLines: false
  })

  staticPromptSections.push({
    title: 'Available Actions',
    contentType: 'bullet',
    content: Object.values(functionType)
  })

  staticPromptSections.push({
    title: 'NOT Available Actions',
    contentType: 'bullet',
    content: [
      'Move (drag/drop) Events',
      'Move (drag/drop) Cards',
      'Add, edit, or delete comments on cards'
    ]
  })

  staticPromptSections.push({
    title: 'Important Notes',
    content: [
      'If the user\'s request corresponds to one of the above actions, respond with a function call.',
      'If not, instead reply in plain text with the message explaining that the action is not available yet. For example if the user asks to move an event up or down, do not call a lane action just because the request contains the word "move" — there is no action to move an event up or down yet.',
      'When the user asks about red/yellow warnings or errors (including specific read models, commands, entities, or the domain model), you MUST call validateDomainModel first and base your answer strictly on its returned issues array. Always include scope; use { "scope": "all" } unless the user asked for a subset.',
      'If the user asks to check ONLY a subset (e.g. "only read models"), call validateDomainModel with the matching scope (e.g. { "scope": "readModels" }) and summarize ONLY that subset.',
      'If any essential detail is missing or ambiguous (for instance, the user says "rename the event" but doesn\'t specify which event), pause and ask a follow-up question. Do not call any function until you have enough information to proceed safely.'
    ]
  })

  staticPromptSections.push({
    title: 'Workflow UI Overview',
    contentType: 'bullet',
    content: [
      '**Workflow Diagram**: At the top of the screen, events appear as "sticky-note" boxes arranged left-to-right in (typically) chronological order. Arrows connect each event to its parent(s).',
      '**Event Cards Sidebar & Backlog Tab**: Each event has associated cards of various types. These cards are visible in a sidebar. There is also a "Backlog" tab that shows only the cards flagged for backlog review.',
      '**Entities Tab**: Under the "Entities" tab, you see all global data models (entities) used in this workflow. Each entity lists its fields (name, cardinality, exampleData, dataType, etc.).',
      '**Domain Model Tab**: Under "Domain Model," read models and write models (commands) appear connected by arrows to the corresponding entities. Commands (write models) show the role (lane) that issues them, the aggregate root entity they modify, and the resulting event. Entities are grouped by aggregate root - multiple commands can attach to the same root entity if they operate on it.'
    ]
  })

  staticPromptSections.push({
    title: 'Card Types',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(cardTypes || [])
  })

  dynamicPromptSections.push({
    title: 'Workflow Metadata',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({
      id: workflow.id,
      name: workflow.name,
      version: workflow.version,
      boundedContexts: (workflow.boundedContexts || []).map((item) => item.name)
    })
  })

  dynamicPromptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildAssistantWorkflowSpec(workflow, boundedContext, cardTypesMap)
  })

  if (collaboratorDrift) {
    dynamicPromptSections.push({
      title: 'Concurrency Notice',
      content: [
        'The workflow was modified by another user while you were working. The specification above already includes their changes alongside your own.'
      ]
    })
  }

  messages.push(createSystemMessage(buildPrompt(staticPromptSections)))
  messages.push(createSystemMessage(buildPrompt(dynamicPromptSections)))

  const outputs = constructMessagesHistory(messagesHistory)

  outputs.forEach((output) => {
    messages.push(output)
  })

  if (prompt) {
    let prefix = ''
    if (currentUIState?.selectedTabNum && typeof currentUIState?.selectedTabNum !== 'undefined' && currentUIState?.selectedTabName) {
      prefix += `[Selected Tab: ${currentUIState.selectedTabNum} - ${currentUIState.selectedTabName}] `
    }
    if (currentUIState?.selectedEventId && currentUIState?.selectedEventName) {
      prefix += `[Selected Event: "${currentUIState.selectedEventId} - ${currentUIState.selectedEventName}"] `
    }
    if (prefix !== '') {
      prefix += '\n'
    }

    messages.push(createUserMessage(prefix + prompt, currentUIState))
  }

  currentTurnOutputs.forEach((output) => {
    messages.push(output)
  })

  return messages
}

export const buildFunctionCallResponsePrompt = ({ prompt, updatesSummary = [], incomplete = false }) => {
  const messages = []

  const systemMessage = incomplete
    ? systemMessages.WORKFLOW_ASSISTANT_SUMMARY_INCOMPLETE
    : systemMessages.WORKFLOW_ASSISTANT_SUMMARY

  messages.push(createSystemMessage(buildPrompt([systemMessage])))

  if (prompt) {
    messages.push(createUserMessage(prompt))
  }

  const recap = buildPrompt([
    {
      title: 'Completed actions',
      contentType: 'bullet',
      content: updatesSummary.length ? updatesSummary : ['Changes were applied.']
    }
  ])

  messages.push(createUserMessage(recap))

  return messages
}
